import React, { useEffect, useRef, useState } from 'react';

const DISMISS_KEY = 'adblock-notice-dismissed-v1';

/**
 * Lightweight ad blocker detector that shows a small, dismissible banner
 * asking users to allow ads on this site. Detection is heuristic and compliant:
 * - Creates a bait element with common ad classes that many blockers hide
 * - Checks existing <ins class="adsbygoogle"> elements for zero height after a short delay
 */
const AdBlockNotice: React.FC = () => {
  const [show, setShow] = useState(false);
  const baitRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Don't show if user dismissed recently
    if (localStorage.getItem(DISMISS_KEY) === '1') return;

    // Create a small bait element that ad blockers often hide
    const bait = document.createElement('div');
    bait.setAttribute('class', 'adsbox adsbygoogle ad-banner');
    bait.setAttribute('style', 'width:1px;height:1px;position:absolute;left:-9999px;top:-9999px;');
    document.body.appendChild(bait);
    baitRef.current = bait;

    const detect = () => {
      let blocked = false;

      // Heuristic 1: bait element hidden by extension
      if (baitRef.current) {
        const baitStyles = window.getComputedStyle(baitRef.current);
        blocked =
          baitStyles.getPropertyValue('display') === 'none' ||
          baitStyles.getPropertyValue('visibility') === 'hidden' ||
          baitRef.current.offsetParent === null;
      }

      // Heuristic 2: adsbygoogle elements exist but have zero height after load
      // Defer DOM reads to avoid forced layout adjustments (25ms each)
      if (!blocked) {
        const adIns = Array.from(document.querySelectorAll('ins.adsbygoogle')) as HTMLDivElement[];
        if (adIns.length > 0) {
          // Defer layout reads using requestIdleCallback to avoid forced reflows
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              requestAnimationFrame(() => {
                const heights = adIns.map((el) => el.offsetHeight || 0);
                const anyCollapsed = heights.some((h) => h === 0);
                const allCollapsed = heights.every((h) => h === 0);
                if (anyCollapsed && allCollapsed) {
                  setShow(true);
                }
              });
            }, { timeout: 100 });
          } else {
            setTimeout(() => {
              requestAnimationFrame(() => {
                const heights = adIns.map((el) => el.offsetHeight || 0);
                const anyCollapsed = heights.some((h) => h === 0);
                const allCollapsed = heights.every((h) => h === 0);
                if (anyCollapsed && allCollapsed) {
                  setShow(true);
                }
              });
            }, 100);
          }
        }
      }

      // Heuristic 3: AdSense script completely blocked (no window.adsbygoogle created)
      if (!blocked) {
        if (typeof (window as any).adsbygoogle === 'undefined') {
          blocked = true;
        }
      }

      // Heuristic 4: No ad-related iframes present after multiple seconds
      if (!blocked) {
        const adIframes = document.querySelectorAll('iframe[src*="googlesyndication"], iframe[src*="doubleclick"]');
        const hasAdIns = document.querySelector('ins.adsbygoogle');
        if (hasAdIns && adIframes.length === 0) {
          blocked = true;
        }
      }

      if (blocked) setShow(true);
    };

    // Try detection shortly after page load and again after a delay
    const t1 = window.setTimeout(detect, 1200);
    const t2 = window.setTimeout(detect, 3500);
    const t3 = window.setTimeout(detect, 6000);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      if (baitRef.current) baitRef.current.remove();
    };
  }, []);

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto z-[9998]">
      <div className="mx-auto max-w-xl rounded-xl border border-orange-200 bg-white/90 backdrop-blur shadow-lg">
        <div className="flex items-start gap-3 p-3">
          <div className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-gradient-to-br from-orange-500 to-purple-600" />
          <div className="text-sm text-gray-800">
            <div className="font-semibold">Please allow ads to support MotivaMatch</div>
            <div className="text-gray-600">
              We keep the experience free by showing a few relevant ads. Kindly whitelist our site in your ad blocker.
            </div>
          </div>
          <button
            onClick={() => {
              localStorage.setItem(DISMISS_KEY, '1');
              setShow(false);
            }}
            className="ml-auto rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdBlockNotice;


